/*
 * The variant picker. Re-requests its section for the chosen option values and
 * swaps the buy-box regions that depend on the variant: each says so with
 * `data-variant-region`, naming itself, on its root. A band
 * away from its product's page names a stand-in section to ask for instead,
 * and the regions it returns are given the band's ids before they are swapped.
 *
 * An asset, not the block's {% javascript %}: the quick-add drawer serves the
 * picker on pages that never rendered the block, and imports this file first.
 * Behaviour: bin/test-variant-picker.mjs.
 */

(function () {
  'use strict';

  class FaubourgVariantPicker extends HTMLElement {
    requestId = 0;
    controller = null;

    connectedCallback() {
      this.setAttribute('data-enhanced', '');

      // Adopted from a scripting-off parse (the theme editor's re-render), the
      // fallback select is a live second `id` field. It has no use here.
      this.querySelectorAll('noscript').forEach((fallback) => fallback.remove());

      this.addEventListener('change', this.handleChange);

      // A selling plan is chosen outside this element but re-prices what is
      // inside it, so the picker listens for it on the buy box it shares.
      this.buyBox = this.closest('[data-buy-box]');
      if (this.buyBox && this.buyBox.querySelector('faubourg-variant-picker') === this) {
        this.buyBox.addEventListener('change', this.handlePlanChange);
      } else {
        this.buyBox = null;
      }

      this.addEventListener('pointerover', this.handlePreview);
      this.addEventListener('focusin', this.handlePreview);
      this.addEventListener('pointerout', this.handlePreviewEnd);
      this.addEventListener('focusout', this.handlePreviewEnd);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      if (this.buyBox) this.buyBox.removeEventListener('change', this.handlePlanChange);
      this.removeEventListener('pointerover', this.handlePreview);
      this.removeEventListener('focusin', this.handlePreview);
      this.removeEventListener('pointerout', this.handlePreviewEnd);
      this.removeEventListener('focusout', this.handlePreviewEnd);
      this.abort();
    }

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    handleChange = (event) => {
      if (!event.target.matches('[data-option-value-id], select')) return;
      this.update(event.target);
    };

    handlePlanChange = (event) => {
      const input = event.target;
      if (!(input instanceof Element) || !input.matches('[name="selling_plan"]')) return;
      this.update(input);
    };

    // Read from the buy box, not the address: a featured band does not own the
    // page's URL, and the server has already dropped a plan the variant lacks.
    planIn(box) {
      const checked = box ? box.querySelector('input[name="selling_plan"]:checked') : null;
      return checked && checked.value ? checked.value : null;
    }

    readoutFor(value) {
      return value.closest('.product-variant-picker__option')?.querySelector('[data-readout]');
    }

    writeReadout(readout, name, note) {
      const nameEl = readout.querySelector('[data-readout-name]');
      const noteEl = readout.querySelector('[data-readout-note]');
      if (nameEl) nameEl.textContent = name;
      if (noteEl) noteEl.textContent = note || '';
    }

    valueFrom(event) {
      const target = event.target;
      if (!(target instanceof Element)) return null;

      const value = target.matches('.product-variant-picker__input')
        ? target.nextElementSibling
        : target.closest('.product-variant-picker__value');

      if (!value || !value.matches?.('.product-variant-picker__value')) return null;
      return this.contains(value) ? value : null;
    }

    handlePreview = (event) => {
      const value = this.valueFrom(event);
      if (!value) return;

      const readout = this.readoutFor(value);
      if (!readout) return;

      // A face in words names itself and is never read back.
      const readBack = value.dataset.valueName !== undefined;
      this.writeReadout(
        readout,
        readBack ? value.dataset.valueName : readout.dataset.defaultName,
        readBack ? value.dataset.valueNote : readout.dataset.defaultNote
      );
    };

    handlePreviewEnd = (event) => {
      const value = this.valueFrom(event);
      if (!value) return;

      // Only a move onto another read-back face of the option keeps the legend.
      const next = this.valueFrom({ target: event.relatedTarget });
      if (next?.dataset.valueName !== undefined && next.parentElement === value.parentElement) return;

      const readout = this.readoutFor(value);
      if (!readout) return;

      this.writeReadout(readout, readout.dataset.defaultName, readout.dataset.defaultNote);
    };

    // One pass over radios and dropdowns, so the ids keep the option order the
    // server writes `data-option-values` in.
    get optionValueIds() {
      const controls = this.querySelectorAll('input[type="radio"]:checked, .product-variant-picker__select');

      return Array.from(controls)
        .map((control) => (control.matches('select') ? control.options[control.selectedIndex] : control))
        .map((element) => (element && element.dataset ? element.dataset.optionValueId : null))
        .filter(Boolean)
        .join(',');
    }

    get section() {
      return this.closest('[data-section-id]');
    }

    get form() {
      return document.getElementById(this.dataset.formId);
    }

    setPending(pending, sectionId) {
      const scope = sectionId ?? this.section?.dataset.sectionId;

      if (pending) {
        this.setAttribute('data-loading', '');
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('data-loading');
        this.removeAttribute('aria-busy');
      }

      document.dispatchEvent(new CustomEvent('variant:pending', { detail: { pending, sectionId: scope } }));

      const form = this.form;
      if (!form) return;

      const region = form.closest('.product-buy-buttons') || form;

      if (pending) {
        region.setAttribute('inert', '');
        region.setAttribute('aria-busy', 'true');
      } else {
        region.removeAttribute('inert');
        region.removeAttribute('aria-busy');
      }
    }

    async update(source) {
      const section = this.section;
      const sectionId = section.dataset.sectionId;
      const productUrl = this.dataset.productUrl;

      const requestedOptionValues = this.optionValueIds;
      const focusId = source.id;

      this.requestId += 1;
      const requestId = this.requestId;

      this.abort();
      const controller = new AbortController();
      this.controller = controller;

      this.setPending(true, sectionId);

      try {
        // A section is re-rendered from its own page. One away from its
        // product's page says so, since option values are only honoured at the
        // product's address: the drawer asks for itself, a band for the
        // stand-in, as the band does not exist there.
        const { renderUrl, renderSection } = section.dataset;
        const target = new URL(renderUrl || window.location.href, window.location.origin);
        if (renderUrl) window.Faubourg.carryPageParams(target);
        target.searchParams.delete('variant');
        target.searchParams.set('section_id', renderSection || sectionId);
        target.searchParams.set('option_values', requestedOptionValues);

        const requestedPlan = this.planIn(this.buyBox || this.closest('[data-buy-box]'));
        if (requestedPlan) target.searchParams.set('selling_plan', requestedPlan);
        else target.searchParams.delete('selling_plan');

        const response = await fetch(target.toString(), { signal: controller.signal });

        if (!response.ok) throw new Error(response.status);

        const text = await response.text();

        if (requestId !== this.requestId) return;

        const document_ = window.Faubourg.parseSection(text);
        const buyBox = section.querySelector('[data-buy-box]');
        const nextBuyBox = document_.querySelector('[data-buy-box]');

        if (!nextBuyBox) throw new Error('missing buy box');

        const nextPicker = nextBuyBox.querySelector('faubourg-variant-picker');
        if (nextPicker && nextPicker.dataset.optionValues !== requestedOptionValues) {
          throw new Error('option values not honoured outside the product template');
        }

        const quantity = this.quantityValue();

        const standIn = renderSection ? document_.querySelector('[data-section-id]')?.dataset.sectionId : null;
        this.replaceRegions(buyBox, nextBuyBox, standIn ? [standIn, sectionId] : null);

        const picker = buyBox.querySelector('faubourg-variant-picker');

        const variantId = picker.dataset.variantId || null;
        const available = picker.dataset.variantAvailable === 'true';
        const mediaId = picker.dataset.variantMedia || null;

        picker.restoreQuantity(quantity);

        // Reached through a collection or not, the page keeps the shopper's own
        // parameters: only the three this element owns are rewritten.
        const path = window.location.pathname.replace(/\/collections\/[^/]+(?=\/products\/)/, '');

        if (path === new URL(productUrl, window.location.origin).pathname) {
          const next = new URL(window.location.href);
          ['variant', 'option_values', 'selling_plan'].forEach((key) => next.searchParams.delete(key));
          if (variantId) next.searchParams.set('variant', variantId);
          else next.searchParams.set('option_values', requestedOptionValues);

          const plan = this.planIn(buyBox);
          if (plan) next.searchParams.set('selling_plan', plan);

          window.history.replaceState({}, '', next.pathname + next.search + next.hash);
        }

        // A plan radio sits in a sibling region, so look in the buy box.
        const restored = focusId ? buyBox.querySelector(`#${CSS.escape(focusId)}`) : null;
        if (restored) restored.focus();

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: { variantId, sectionId, productUrl, available, mediaId },
          })
        );

        this.announce(section, picker);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (requestId !== this.requestId) return;

        const destination = new URL(productUrl, window.location.origin);
        window.Faubourg.carryPageParams(destination);
        destination.searchParams.set('option_values', requestedOptionValues);

        window.location.assign(destination.toString());
      } finally {
        if (requestId === this.requestId) this.setPending(false, sectionId);
      }
    }

    // A swap moves nothing a screen reader follows, so the section says what
    // the shopper now has, in the server's words.
    announce(section, picker) {
      const status = section.querySelector('[data-product-status]');
      const price = section.querySelector('[data-buy-box] .price__current');
      if (status && picker.dataset.status) {
        status.textContent = picker.dataset.status.replace('[price]', price ? price.textContent.trim() : '');
      }
    }

    // Each region is paired, in order, with one of its name in the response:
    // none is placed or removed, so one the stand-in lacks is left alone. A
    // region renders its root for every variant, hidden when it has nothing.
    replaceRegions(buyBox, nextBuyBox, sectionIds) {
      const regions = (box, name) => box.querySelectorAll(`[data-variant-region="${CSS.escape(name)}"]`);
      const names = new Set(Array.from(buyBox.querySelectorAll('[data-variant-region]'), (r) => r.dataset.variantRegion));

      for (const name of names) {
        const current = regions(buyBox, name);
        const next = regions(nextBuyBox, name);

        const shared = Math.min(current.length, next.length);
        for (let i = 0; i < shared; i += 1) {
          if (sectionIds) {
            this.rescope(next[i], [sectionIds, [next[i].dataset.blockId, current[i].dataset.blockId]], current[i]);
          }
          current[i].outerHTML = next[i].outerHTML;
        }
      }
    }

    // A stand-in's region is named after the stand-in's section and block.
    // Renamed to the band's, its form, labels and drawer point into the band,
    // however many bands share the page; the theme editor's handle on the
    // block is the band's own.
    rescope(region, renames, current) {
      const pairs = renames.filter(([from, to]) => from && to && from !== to);
      for (const element of [region, ...region.querySelectorAll('*')]) {
        for (const { name, value } of Array.from(element.attributes)) {
          if (name === 'class') continue;
          const next = name.startsWith('data-shopify-editor') && element === region
            ? current.getAttribute(name) ?? value
            : pairs.reduce((text, [from, to]) => text.split(from).join(to), value);
          if (next !== value) element.setAttribute(name, next);
        }
      }
    }

    quantityValue() {
      const section = this.section;
      const input = section.querySelector('.quantity-selector__input');

      if (input && !input.disabled) {
        section.dataset.lastQuantity = input.value;
        return input.value;
      }

      return section.dataset.lastQuantity ?? null;
    }

    restoreQuantity(value) {
      if (value === null) return;

      const input = this.section.querySelector('.quantity-selector__input');
      if (!input || input.disabled) return;

      const wanted = Number(value);
      if (!Number.isFinite(wanted)) return;

      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const step = Number(input.step) > 0 ? Number(input.step) : 1;

      let next = min + Math.floor((wanted - min) / step) * step;
      if (next < min) next = min;
      if (next > max) next = min + Math.floor((max - min) / step) * step;

      input.value = String(next);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  if (!customElements.get('faubourg-variant-picker')) {
    customElements.define('faubourg-variant-picker', FaubourgVariantPicker);
  }
})();
